import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
import {
  getGebruikers,
  maakGebruiker,
  wijzigGebruiker,
  resetGebruikerWachtwoord,
} from '../api/api';

const ROLLEN = ['beheerder', 'behandelaar', 'lezer'];

function formatteerDatum(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

export default function Gebruikers() {
  const { gebruiker } = useAuth();
  const [gebruikers, setGebruikers] = useState([]);
  const [laden, setLaden] = useState(true);
  const [melding, setMelding] = useState(null);

  const [toonNieuw, setToonNieuw] = useState(false);
  const [nieuw, setNieuw] = useState({
    voornaam: '', tussenvoegsel: '', achternaam: '', email: '', rol: 'behandelaar', wachtwoord: '',
  });
  const [nieuwBezig, setNieuwBezig] = useState(false);

  const laad = useCallback(async () => {
    try {
      const res = await getGebruikers();
      setGebruikers(res.data);
    } catch {
      setMelding({ type: 'error', tekst: 'Gebruikers konden niet worden geladen.' });
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { laad(); }, [laad]);

  const meld = (type, tekst) => setMelding({ type, tekst });

  const maakAan = async () => {
    setMelding(null);
    if (!nieuw.voornaam.trim() || !nieuw.achternaam.trim()) return meld('error', 'Voornaam en achternaam zijn verplicht.');
    if (!nieuw.email.trim()) return meld('error', 'E-mail is verplicht.');
    if (nieuw.wachtwoord.length < 10) return meld('error', 'Begin-wachtwoord moet minstens 10 tekens zijn.');
    setNieuwBezig(true);
    try {
      await maakGebruiker({
        voornaam: nieuw.voornaam.trim(),
        tussenvoegsel: nieuw.tussenvoegsel.trim(),
        achternaam: nieuw.achternaam.trim(),
        email: nieuw.email.trim(),
        rol: nieuw.rol,
        wachtwoord: nieuw.wachtwoord,
      });
      meld('ok', `Gebruiker ${nieuw.voornaam} aangemaakt.`);
      setNieuw({ voornaam: '', tussenvoegsel: '', achternaam: '', email: '', rol: 'behandelaar', wachtwoord: '' });
      setToonNieuw(false);
      await laad();
    } catch (err) {
      meld('error', err?.response?.data?.detail || 'Aanmaken mislukt.');
    } finally {
      setNieuwBezig(false);
    }
  };

  const veranderRol = async (g, rol) => {
    setMelding(null);
    try {
      await wijzigGebruiker(g.gebruiker_id, { rol });
      await laad();
    } catch (err) {
      meld('error', err?.response?.data?.detail || 'Rol wijzigen mislukt.');
    }
  };

  const toggleActief = async (g) => {
    setMelding(null);
    try {
      await wijzigGebruiker(g.gebruiker_id, { actief: !g.actief });
      await laad();
    } catch (err) {
      meld('error', err?.response?.data?.detail || 'Wijzigen mislukt.');
    }
  };

  const resetWachtwoord = async (g) => {
    setMelding(null);
    const nw = window.prompt(`Nieuw begin-wachtwoord voor ${g.naam} (min. 10 tekens):`, '');
    if (nw === null) return;
    if (nw.length < 10) return meld('error', 'Wachtwoord moet minstens 10 tekens zijn.');
    try {
      await resetGebruikerWachtwoord(g.gebruiker_id, nw);
      meld('ok', `Wachtwoord van ${g.naam} gereset. Geef het nieuwe wachtwoord door; de gebruiker kan het daarna zelf wijzigen.`);
    } catch (err) {
      meld('error', err?.response?.data?.detail || 'Reset mislukt.');
    }
  };

  const veld = (k) => (e) => setNieuw((s) => ({ ...s, [k]: e.target.value }));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Gebruikers</h1>
          <p className="page-subtitle">Beheer wie toegang heeft tot het register</p>
        </div>
        <button className="btn btn-primary" onClick={() => setToonNieuw((v) => !v)}>
          {toonNieuw ? 'Annuleren' : 'Nieuwe gebruiker'}
        </button>
      </div>

      {melding && (
        <div className={`alert ${melding.type === 'ok' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>
          {melding.tekst}
        </div>
      )}

      {toonNieuw && (
        <div className="card" style={{ marginBottom: 20, maxWidth: 720 }}>
          <div className="card-header"><h2 className="card-title">Nieuwe gebruiker</h2></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label className="field-label">Voornaam</label><input className="input" value={nieuw.voornaam} onChange={veld('voornaam')} /></div>
            <div><label className="field-label">Tussenvoegsel (optioneel)</label><input className="input" value={nieuw.tussenvoegsel} onChange={veld('tussenvoegsel')} /></div>
            <div><label className="field-label">Achternaam</label><input className="input" value={nieuw.achternaam} onChange={veld('achternaam')} /></div>
            <div><label className="field-label">E-mail</label><input className="input" type="email" value={nieuw.email} onChange={veld('email')} /></div>
            <div>
              <label className="field-label">Rol</label>
              <select className="input" value={nieuw.rol} onChange={veld('rol')}>
                {ROLLEN.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div><label className="field-label">Begin-wachtwoord (min. 10 tekens)</label><input className="input" type="text" value={nieuw.wachtwoord} onChange={veld('wachtwoord')} /></div>
          </div>
          <button className="btn btn-primary" onClick={maakAan} disabled={nieuwBezig} style={{ marginTop: 16 }}>
            {nieuwBezig ? <><span className="spinner" /> Aanmaken...</> : 'Gebruiker aanmaken'}
          </button>
        </div>
      )}

      <div className="card">
        {laden ? (
          <div style={{ padding: 24, textAlign: 'center' }}><span className="spinner" /></div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Naam</th><th>E-mail</th><th>Rol</th><th>Status</th><th>Laatst ingelogd</th><th></th>
                </tr>
              </thead>
              <tbody>
                {gebruikers.map((g) => {
                  const isZelf = g.gebruiker_id === gebruiker?.gebruiker_id;
                  return (
                    <tr key={g.gebruiker_id} style={{ opacity: g.actief ? 1 : 0.55 }}>
                      <td>{g.naam}{isZelf && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> (jij)</span>}</td>
                      <td>{g.email}</td>
                      <td>
                        <select
                          className="input"
                          value={g.rol}
                          onChange={(e) => veranderRol(g, e.target.value)}
                          disabled={isZelf}
                          style={{ height: 34, fontSize: 13, padding: '0 8px' }}
                          title={isZelf ? 'Je kunt je eigen rol niet wijzigen' : ''}
                        >
                          {ROLLEN.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </td>
                      <td>{g.actief ? 'Actief' : 'Inactief'}</td>
                      <td>{formatteerDatum(g.laatst_ingelogd_op)}</td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-ghost" style={{ fontSize: 13, padding: '4px 10px' }} onClick={() => resetWachtwoord(g)}>
                          Wachtwoord
                        </button>
                        <button
                          className="btn btn-ghost"
                          style={{ fontSize: 13, padding: '4px 10px', color: g.actief ? 'var(--danger)' : 'var(--accent)' }}
                          onClick={() => toggleActief(g)}
                          disabled={isZelf}
                          title={isZelf ? 'Je kunt jezelf niet deactiveren' : ''}
                        >
                          {g.actief ? 'Deactiveren' : 'Activeren'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
